import React, { useState } from 'react';
import { Box, Button, Card, CardContent, Typography, Collapse, Link } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DescriptionIcon from '@mui/icons-material/Description';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import LanguageIcon from '@mui/icons-material/Language';
import { Citation } from '@/types/citations';

interface MessageCitationsProps {
  citations: Citation[];
}

export const MessageCitations: React.FC<MessageCitationsProps> = ({ citations }) => {
  const [expanded, setExpanded] = useState(false);
  const [expandedCitations, setExpandedCitations] = useState<Set<number>>(new Set());

  if (!citations || citations.length === 0) {
    return null;
  }

  const toggleCitation = (index: number) => {
    setExpandedCitations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const truncateSnippet = (snippet: string, maxLength: number = 200) => {
    if (snippet.length <= maxLength) return snippet;
    return snippet.substring(0, maxLength) + '...';
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Button
        onClick={() => setExpanded(!expanded)}
        startIcon={<MenuBookIcon />}
        endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        variant="outlined"
        size="small"
        sx={{ 
          textTransform: 'none',
          borderRadius: '20px',
          px: 2
        }}
      >
        Sources ({citations.length})
      </Button>

      <Collapse in={expanded}>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {citations.map((citation, index) => (
            <Card 
              key={index} 
              variant="outlined" 
              id={`citation-${index}`}
              sx={{ 
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: 1,
                scrollMarginTop: '80px' // Offset for fixed headers
              }}
            >
              <CardContent>
                {/* Document Citation */}
                {citation.type === 'document' && (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      {/* Citation Number */}
                      <Box
                        sx={{
                          minWidth: '22px',
                          height: '22px',
                          borderRadius: '50%',
                          backgroundColor: 'primary.main',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold',
                          fontSize: '0.75rem',
                          mt: 0.5,
                          flexShrink: 0
                        }}
                      >
                        {index + 1}
                      </Box>
                      
                      <Box sx={{ flex: 1 }}>
                        {/* Clickable Filename */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <DescriptionIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                          {citation.onedrive_url || citation.file_path ? (
                            <Link
                              href={citation.onedrive_url || `file:///${citation.file_path?.replace(/\\/g, '/')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                textDecoration: 'none',
                                fontWeight: 600,
                                color: 'primary.main',
                                '&:hover': { textDecoration: 'underline' }
                              }}
                            >
                              {citation.filename}
                              {citation.page && ` · Page ${citation.page}`}
                            </Link>
                          ) : (
                            <Typography variant="subtitle2" fontWeight="bold">
                              {citation.filename}
                              {citation.page && ` · Page ${citation.page}`}
                            </Typography>
                          )}
                        </Box>
                        
                        {/* Snippet - Always show unless it's just a placeholder */}
                        {citation.snippet && !citation.snippet.startsWith('Referenced document:') && (
                          <>
                            <Typography 
                              variant="body2" 
                              color="text.secondary" 
                              sx={{ 
                                fontStyle: 'italic',
                                backgroundColor: 'action.hover',
                                padding: 1.5,
                                borderRadius: 1,
                                borderLeft: '3px solid',
                                borderLeftColor: 'primary.main',
                                lineHeight: 1.6
                              }}
                            >
                              "{expandedCitations.has(index) 
                                ? citation.snippet 
                                : truncateSnippet(citation.snippet)}"
                            </Typography>
                            {citation.snippet.length > 200 && (
                              <Button
                                size="small"
                                onClick={() => toggleCitation(index)}
                                sx={{ mt: 1, textTransform: 'none', fontSize: '0.813rem' }}
                              >
                                {expandedCitations.has(index) ? 'Show less' : 'Read more'}
                              </Button>
                            )}
                          </>
                        )}
                      </Box>
                    </Box>
                  </>
                )}

                {/* Web Citation */}
                {citation.type === 'web' && (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <LanguageIcon sx={{ mr: 1, color: 'info.main', fontSize: 20 }} />
                      <Typography variant="subtitle2" fontWeight="bold">
                        {citation.title || citation.domain || 'Web Source'}
                      </Typography>
                    </Box>
                    {citation.url && (
                      <Link 
                        href={citation.url} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        sx={{ 
                          fontSize: '0.875rem', 
                          mb: 1, 
                          display: 'block',
                          wordBreak: 'break-all'
                        }}
                      >
                        {citation.url} ↗
                      </Link>
                    )}
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ 
                        fontStyle: 'italic',
                        backgroundColor: 'action.hover',
                        padding: 1.5,
                        borderRadius: 1,
                        borderLeft: '3px solid',
                        borderLeftColor: 'info.main'
                      }}
                    >
                      "{expandedCitations.has(index) 
                        ? citation.snippet 
                        : truncateSnippet(citation.snippet)}"
                    </Typography>
                    {citation.snippet.length > 200 && (
                      <Button
                        size="small"
                        onClick={() => toggleCitation(index)}
                        sx={{ mt: 1, textTransform: 'none' }}
                      >
                        {expandedCitations.has(index) ? 'Show less' : 'Show more'}
                      </Button>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};
